import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/api.js';
import { LanguageButton, ThemeButton } from '../App.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';

/**
 * Who am I signed in as, and into which account?
 *
 * The chip alone showed a display name, which is not enough the moment anyone
 * works with two accounts at once — an operator testing a customer's tenant
 * beside their own has two windows that look identical, and the only thing
 * distinguishing them is which data happens to be on screen. That is a state
 * worth being sure of before pressing publish.
 *
 * So the chip opens: the account name, the plan, the email, and the role.
 */
export function AccountMenu({
  displayName,
  email,
  role,
  initials,
  build,
  onSignOut,
}: {
  displayName: string;
  email: string;
  role: string;
  initials: string;
  build: {
    environment: string;
    isProduction: boolean;
    commit: string | null;
    builtAt: string | null;
  };
  onSignOut: () => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Only fetched once the menu is opened: the tenant's name and plan are not
  // needed to render a closed chip, and this runs on every page of the app.
  const account = useQuery({
    queryKey: ['account'],
    queryFn: api.account,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const tenant = account.data?.tenant;

  return (
    <div className="account-menu" ref={container}>
      <button
        type="button"
        className="user-chip"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
      >
        <span className="avatar">{initials}</span>
        <span className="chip-name">{displayName}</span>
        <Icon.chevron className={open ? 'chip-caret open' : 'chip-caret'} />
      </button>

      {open && (
        <div className="account-popover" role="menu">
          <div className="account-popover-head">
            <span className="avatar large">{initials}</span>
            <div>
              <strong>{displayName}</strong>
              <div className="mono tiny muted ltr">{email}</div>
            </div>
          </div>

          {/* Set once and then forgotten, so they live behind a press rather
              than spending a permanent slot in a header that now also carries
              the trail. */}
          <div className="menu-switches">
            <LanguageButton />
            <ThemeButton />
          </div>

          <dl className="account-facts">
            <dt>{t('menu.account')}</dt>
            <dd>
              {/* The account, not the person. Two people in one tenant see the
                  same value here, and that is the point of showing it. */}
              {account.isLoading ? '…' : (tenant?.name ?? '—')}
            </dd>

            <dt>{t('menu.plan')}</dt>
            <dd>{account.isLoading ? '…' : (tenant?.plan.name ?? '—')}</dd>

            {/* Which build is answering. Beside the account rather than in a
                footer: the question "is this the one I published?" is asked at
                the same moment as "which account am I in", and by the same
                person. */}
            <dt>{t('menu.environment')}</dt>
            <dd>
              <span className={build.isProduction ? 'badge' : 'badge warn'}>
                {build.environment}
              </span>
            </dd>

            {build.commit && (
              <>
                <dt>{t('menu.build')}</dt>
                <dd className="mono tiny ltr">{build.commit}</dd>
              </>
            )}

            <dt>{t('menu.role')}</dt>
            <dd>
              <span className={role === 'SUPERADMIN' ? 'badge warn' : 'badge'}>
                {t(`account.role.${role}` as never)}
              </span>
            </dd>

            {tenant && tenant.status !== 'ACTIVE' && (
              <>
                <dt>{t('common.status')}</dt>
                <dd>
                  <span className="badge warn">{tenant.status}</span>
                </dd>
              </>
            )}
          </dl>

          <button type="button" className="ghost account-signout" onClick={onSignOut}>
            <Icon.logout />
            {t('app.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}
